import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import CtaButtonGroup, {
  CtaActionType,
} from "@/components/molecules/CtaButtonGroup";
import LeadGenerationInputs from "@/components/organisms/LeadGenerationInputs";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import useThemeStore from "@/shared/store/useThemeStore";
import { getContent } from "@/shared/utils/content.utils";
import { THEMES } from "@shared-types/themes";
import Image, { StaticImageData } from "next/image";
import React, { useMemo } from "react";

type HighlightsFormat = {
  title: string;
  icon?: string;
  leftIcon?: string;
  description: string;
};

type HeaderDataFormat = {
  footer?: boolean;
  ctaRedirectLink?: string | undefined;
  ctaRedirect?: string;
  type: string;
  id: string;
  gradientColor: string;
  width: string;
  height: string;
  featureImage?: string;
  defaultCountry?: string;
  nextImage?: {
    src: StaticImageData;
    alt: string;
  };
  title: string | any[];
  subTitle?: string;
  description: string | HTMLElement;
  ctaText: string;
  ctaType: string;
  ctaLink?: string;
  highlights: HighlightsFormat[];
  learnMoreLink?: string;
  ampBookBtnSrc?: string;
  showBookBtn?: boolean;
};

export interface FeatureProps {
  data: HeaderDataFormat;
  langFile?: string;
}

const Feature: React.FC<FeatureProps> = ({ data, langFile }: FeatureProps) => {
  const id = data.id;
  const isRight = parseInt(id) % 2;
  const { theme } = useThemeStore();
  const isFooter = data?.footer ?? false;

  const renderFeatureTitle = useMemo(() => {
    const title = data.title;

    if (theme === THEMES["02"])
      return (
        <h3 className="mb-6 max-w-4xl text-title-24 text-sapphire01 md:mb-9 lg:text-title-32">
          {getContent(title)}
        </h3>
      );

    return (
      <h3 className="font-obviously mb-6 max-w-4xl text-title-24 font-bold text-dark  md:mb-9 lg:text-title-32">
        {getContent(title)}
      </h3>
    );
  }, [data.gradientColor, data?.id, data.title, isRight, theme]);

  const renderFeatureDescription = useMemo(() => {
    const content = data?.description;
    const className =
      theme === THEMES["02"]
        ? "text-sapphire02 text-2xl leading-snug mb-6 md:mb-9"
        : "text-slate02 leading-6 mb-6 md:mb-8 max-w-sm";
    if (data.description) {
      // TODO: Abhishek => Ask @rajat: Any specific reason we did dangerouslySetInnerHTML here ?
      //@ts-ignore
      return <h4 className={className}>{content}</h4>;
    }
    return null;
  }, [data.description, data?.id, theme]);

  const renderFeatureHighLights = useMemo(() => {
    // const containerClass = theme === THEMES["02"] ? "" : "max-w-xs";
    const titleClass =
      theme === THEMES["02"]
        ? "text-title-14 text-sapphire01  text-black mb-1"
        : "font-obviously text-title-14 text-black mb-1";
    const descriptionClass =
      theme === THEMES["02"]
        ? "text-body-12 text-grey-3"
        : "text-body-12 text-grey-3";
    if (data.highlights)
      return (
        <div className="mt-5 flex flex-col gap-4 space-y-3 xl:grid xl:grid-cols-2">
          {data.highlights.map((highlight, index) => (
            <div
              className="flex items-center  rounded-xl border border-grey-2 px-3 py-4"
              key={highlight.title}
            >
              {highlight?.leftIcon && (
                <img
                  src={highlight.leftIcon}
                  alt="Highlight Icon"
                  className="mr-5 mt-1 pt-0.5"
                />
              )}
              <div className="">
                <h5 className={titleClass}>{highlight.title}</h5>
                <h6 className={descriptionClass}>{highlight.description}</h6>
              </div>
            </div>
          ))}
        </div>
      );
    return null;
  }, [data.highlights, theme]);

  return (
    // <Waypoint
    //   // onEnter={() => {
    //   //   setActiveSection(id);
    //   // }}
    //   // onLeave={() => activeSection === "1" && setActiveSection(null)}
    //   fireOnRapidScroll
    //   topOffset="50px"
    // >
    <div className="relative normal-case">
      <div className="absolute -top-52 opacity-0" id={id}>
        <span id="features"></span>
      </div>
      <section
        className={`relative flex ${
          isRight ? "lg:flex-row" : "lg:flex-row-reverse"
        } container flex-col items-center md:justify-center lg:items-start lg:justify-start`}
      >
        <div className="mb-16 block lg:hidden">
          {data.nextImage?.src ? (
            <Image
              src={data.nextImage?.src}
              alt={data.nextImage?.alt}
              width={500}
              height={500}
              // sizes="100vw"
              // className="mt-4 h-auto w-full md:mt-0 md:h-[228px] md:w-[350px]"
            />
          ) : (
            <img
              src={`${data.featureImage}`}
              alt={`${data.title} image`}
              width={500}
              // quality={90}
              height={500}
              // layout="intrinsic"
              // className="object-contain"
            />
          )}
        </div>
        <div className="flex max-w-[428px] flex-col">
          {data.subTitle && (
            <h5 className="mb-2 text-xs uppercase leading-4 tracking-ultrawide text-slate01">
              {data.subTitle}
            </h5>
          )}
          {renderFeatureTitle}
          {renderFeatureDescription}
          <div className="flex flex-col justify-center md:flex-row md:items-start md:justify-start">
            {data?.showBookBtn && (
              <CtaButtonGroup
                ctas={[
                  {
                    type: ButtonTypeEnum.Primary,
                    size: ButtonSizeEnum.Regular,
                    text: data?.ctaText,
                    actionType:
                      data?.ctaRedirect === "link"
                        ? CtaActionType.RedirectHref
                        : CtaActionType.SignUp,
                    href: data?.ctaRedirectLink,
                    amplitudeEvent: {
                      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                      eventProperties: {
                        source: "marketing_features",
                      },
                    },
                  },
                  {
                    type: ButtonTypeEnum.Secondary,
                    size: ButtonSizeEnum.Regular,
                    text: "Book a Demo",
                    actionType: CtaActionType.BookDemo,
                  },
                ]}
              />
            )}
          </div>
          {data?.ctaType === "schedule-demo" && (
            <a href={data?.ctaLink}>
              <CtaButtonGroup
                ctas={[
                  {
                    type: ButtonTypeEnum.Primary,
                    size: ButtonSizeEnum.Regular,
                    text: "Book a Demo",
                    actionType: CtaActionType.BookDemo,
                    amplitudeEvent: {
                      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                      eventProperties: {
                        source: "marketing_features",
                      },
                    },
                  },
                ]}
              />
            </a>
          )}
        
          {renderFeatureHighLights}

          {data?.ctaType == "lead-generation" && (
            <div
            className="mt-4"
            >
              <LeadGenerationInputs
                isFooter
                // placeholderText={emailPlaceholderText}
                emailOnly={false}
                validateMobile
                footerEvent={isFooter}
                header
                ctaText={data?.ctaText}
                defaultCountry={data?.defaultCountry}
                sourceSent="body_end"
              />
            </div>
          )}

          {data?.ctaType === "link" && (
            <a href={data?.ctaLink}>
              <CtaButtonGroup
                ctas={[
                  {
                    type: ButtonTypeEnum.Primary,
                    size: ButtonSizeEnum.Regular,
                    text: "Book a Demo",
                    actionType: CtaActionType.BookDemo,
                    amplitudeEvent: {
                      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                      eventProperties: {
                        source: "marketing_features",
                      },
                    },
                  },
                ]}
              />
            </a>
          )}
        </div>
        <div
          className={`relative right-0 top-36 mt-2 hidden max-w-[500px] scale-110 transform lg:sticky lg:block ${
            isRight ? "ml-20" : "mr-40"
          }`}
          style={{ width: `100%` }}
        >
          {data.nextImage?.src ? (
            <Image
              src={data.nextImage?.src}
              alt={data.nextImage?.alt}
              width={500}
              height={500}
              // sizes="100vw"
              // className="mt-4 h-auto w-full md:mt-0 md:h-[228px] md:w-[350px]"
            />
          ) : (
            <img
              src={data.featureImage}
              alt={`${data.title} image`}
              // quality={90}
              // priority
              width={parseInt(data.width)}
              height={parseInt(data.height)}
              // layout="responsive"
              className="relative object-contain"
            />
          )}
        </div>
      </section>
    </div>
    // </Waypoint>
  );
};

export default Feature;
